import React from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import CumulativeGgrChart from './CumulativeGgrChart';
import { HeroSkeleton } from './DashboardSkeleton';
import { Card, DeltaPill, InlineError, LegendDot, Skeleton, TextButton } from './ui';
import { VERTICAL_STYLES, valueTone } from './theme';
import { formatBeirutTime, formatExactMoney, formatInteger, formatMoney, formatPercent, formatSignedMoney } from './format';
import { bucketLabel } from './periodText';
import { computeDelta, ggrShares, holdRate } from '../../utils/dashboardMetrics';

const VerticalTile = ({ label, value, share, vertical, currency }) => (
  <div className="min-w-0 rounded-xl bg-white/80 px-3 py-2.5 ring-1 ring-gray-900/5 dark:bg-gray-900/40 dark:ring-white/5">
    <div className="flex items-center justify-between gap-2 text-xs text-gray-500 dark:text-gray-400">
      <span className="flex min-w-0 items-center gap-1.5">
        <LegendDot className={VERTICAL_STYLES[vertical].dot} />
        <span className="truncate">{label}</span>
      </span>
      {share !== null && <span className="tabular-nums text-gray-400 dark:text-gray-500">{formatPercent(share, 0)}</span>}
    </div>
    <p className={`mt-1 truncate text-lg font-semibold tabular-nums ${valueTone(value)}`} title={formatExactMoney(value, currency)}>
      {formatMoney(value, currency)}
    </p>
  </div>
);

const Stat = ({ label, children }) => (
  <div className="min-w-0">
    <dt className="truncate text-xs text-gray-500 dark:text-gray-400">{label}</dt>
    <dd className="mt-0.5 truncate text-base font-semibold tabular-nums text-gray-900 dark:text-white">{children}</dd>
  </div>
);

/** The headline: GGR for the period, its change, the casino/sport split, the chart and three supporting figures. */
export default function GgrHeroCard({ t, language, currency, windows, text, metrics, players, status, error, fetchedAt, onRefresh, busy, className = '' }) {
  const tint = 'bg-gradient-to-b from-teal-50/80 via-white to-white dark:from-teal-500/[0.07] dark:via-gray-800/70 dark:to-gray-800/70';

  if (status === 'loading' || (status === 'error' && !metrics)) {
    return (
      <Card className={`${tint} ${className}`}>
        {status === 'error' ? (
          <div className="space-y-3">
            <InlineError>{error || t('loadFailed', 'Could not load the numbers.')}</InlineError>
            <TextButton onClick={onRefresh} className="text-sm">{t('retry', 'Try again')}</TextButton>
          </div>
        ) : (
          <HeroSkeleton />
        )}
      </Card>
    );
  }

  const { summary, previousToDate, series } = metrics;
  const delta = computeDelta(summary.ggr, previousToDate.ggr);
  const shares = ggrShares(summary);
  const hold = holdRate(summary);
  const formatValue = (value) => formatMoney(value, currency);

  return (
    <Card className={`${tint} ${className}`}>
      <div className="flex items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
          {windows.id === 'today' && <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden="true" />}
          {t('ggrFor', 'GGR · {{period}}', { period: text.name })}
        </h2>
        {fetchedAt && (
          <button
            type="button"
            onClick={onRefresh}
            disabled={busy}
            title={t('refresh', 'Refresh')}
            aria-label={`${t('refresh', 'Refresh')} · ${t('updatedAt', 'Updated {{time}}', { time: formatBeirutTime(fetchedAt) })}`}
            className="-me-1.5 inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-[11px] tabular-nums text-gray-400 transition-colors hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 disabled:opacity-60 dark:text-gray-500 dark:hover:text-gray-300"
          >
            <ArrowPathIcon className={`h-3.5 w-3.5 ${busy ? 'animate-spin motion-reduce:animate-none' : ''}`} aria-hidden="true" />
            {formatBeirutTime(fetchedAt)}
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,19rem)_minmax(0,1fr)] lg:gap-x-10">
        <div className="lg:col-start-1 lg:row-start-1">
          <p
            className={`mt-2 text-4xl font-bold tracking-tight tabular-nums ${valueTone(summary.ggr)}`}
            title={formatExactMoney(summary.ggr, currency)}
          >
            {formatValue(summary.ggr)}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <DeltaPill delta={delta} formatAbs={(value) => formatSignedMoney(value, currency)} />
            <span>{text.comparison}</span>
          </div>

          {error && <div className="mt-3"><InlineError>{error}</InlineError></div>}

          <div className="mt-5 grid grid-cols-2 gap-3">
            <VerticalTile label={t('casino', 'Casino')} value={summary.casino.ggr} share={shares?.casino ?? null} vertical="casino" currency={currency} />
            <VerticalTile label={t('sport', 'Sport')} value={summary.sport.ggr} share={shares?.sport ?? null} vertical="sport" currency={currency} />
          </div>
        </div>

        <div className="mt-5 lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:mt-4 lg:self-end">
          <CumulativeGgrChart
            series={series}
            buckets={windows.current.buckets}
            bucketType={windows.current.bucket}
            nowIndex={windows.current.nowIndex}
            isLive={windows.isLive}
            formatValue={formatValue}
            formatSignedValue={(value) => formatSignedMoney(value, currency)}
            formatBucketLabel={(bucket) => bucketLabel(bucket, language)}
            labels={{
              casino: t('casino', 'Casino'),
              sport: t('sport', 'Sport'),
              now: t('chartNow', 'Now'),
              bucketTotal: text.bucketTotal,
              empty: t('noBetsYet', 'No bets in this period yet'),
              summary: t('chartSummary', 'Cumulative GGR: {{total}} ({{casino}} casino, {{sport}} sport).', {
                total: formatValue(summary.ggr),
                casino: formatValue(summary.casino.ggr),
                sport: formatValue(summary.sport.ggr)
              })
            }}
          />
        </div>

        <dl className="mt-4 grid grid-cols-3 gap-4 border-t border-gray-100 pt-4 dark:border-gray-700/60 lg:col-start-1 lg:row-start-2 lg:mt-5 lg:self-end">
          <Stat label={t('turnover', 'Turnover')}>{formatValue(summary.turnover)}</Stat>
          <Stat label={t('activePlayers', 'Active players')}>
            {players.status === 'loading' ? <Skeleton className="mt-1 h-4 w-10" /> : players.ranking ? formatInteger(players.ranking.activeCount) : '—'}
          </Stat>
          <Stat label={t('holdRate', 'Hold')}>{hold === null ? '—' : formatPercent(hold)}</Stat>
        </dl>
      </div>
    </Card>
  );
}
